import React, { Suspense, useEffect, useState, ReactNode, useMemo } from 'react';
import { Layout, Menu, Typography, Spin, Alert, ConfigProvider, theme } from 'antd';
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import UserContext from '../contexts/user';
import { Navbar } from './navbar';
import { fetchUserContext } from '../tevapi/io';
import { LoadingStates } from './LoadingStates';
import '../../styles/components/tev3.scss';

const { Header, Content, Footer } = Layout;
const { useToken } = theme;

// Define user context type
export interface UserContextType {
  pk?: number;
  username?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  is_staff: boolean;
  is_superuser?: boolean;
  is_authenticated?: boolean;
  is_anonymous?: boolean;
  groups?: string[];
  permissions?: string[];
}

interface TEV3LayoutProps {
  children: ReactNode;
}

interface LayoutState {
  user: UserContextType | null;
  loading: boolean;
  error: Error | null;
}

const TEV3Layout: React.FC<TEV3LayoutProps> = ({ children }) => {
  const [state, setState] = useState<LayoutState>({
    user: null,
    loading: true,
    error: null,
  });

  const { token } = useToken();

  // Fetch user context on mount
  useEffect(() => {
    let mounted = true;

    const loadUserContext = async () => {
      try {
        setState(prev => ({ ...prev, loading: true, error: null }));
        const response = await fetchUserContext();

        if (mounted) {
          setState({
            user: response.data,
            loading: false,
            error: null,
          });
        }
      } catch (error) {
        console.error('Failed to fetch user context:', error);
        if (mounted) {
          setState({
            user: { is_staff: false }, // Provide minimal user object on error
            loading: false,
            error: error as Error,
          });
        }
      }
    };

    loadUserContext();

    return () => {
      mounted = false;
    };
  }, []);

  // User context value for navbar and providers

  // Handle loading state
  if (state.loading) {
    return (
      <Layout style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingStates.FullPage message="Loading TEV3..." />
      </Layout>
    );
  }

  // Handle error state
  if (state.error && !state.user) {
    return (
      <Layout style={{ minHeight: '100vh', padding: '50px' }}>
        <Alert
          message="Failed to load application"
          description="Unable to fetch user context. Please refresh the page or contact support."
          type="error"
          showIcon
          action={
            <button
              onClick={() => window.location.reload()}
              style={{
                background: token.colorPrimary,
                color: token.colorTextLightSolid,
                border: 'none',
                padding: '4px 16px',
                borderRadius: token.borderRadius,
                cursor: 'pointer',
              }}
            >
              Refresh Page
            </button>
          }
        />
      </Layout>
    );
  }

  const userContext = state.user || { is_staff: false };

  return (
    <ConfigProvider
      theme={{
        token: {
          // Add custom theme tokens here if needed
          // colorPrimary: '#1890ff',
        },
      }}
    >
      <UserContext.Provider value={userContext}>
        <Layout className="tev3-layout" style={{ minHeight: '100vh' }}>
          <Header className="tev3-header" style={{
            padding: 0,
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            width: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            background: token.colorBgContainer,
          }}>
            <div className="tev3-header-content">
              <NavLink to="/" className="tev3-logo">
                {/* Logo placeholder - can be replaced with actual logo */}
                <Typography.Title level={4} style={{ margin: 0, color: 'white' }}>
                  TEV3
                </Typography.Title>
              </NavLink>
              <Navbar user={userContext} />
            </div>
          </Header>

          <Content className="tev3-content" style={{
            padding: 0,
            background: token.colorBgLayout,
          }}>
            <Suspense fallback={<LoadingStates.Content />}>
              <div className="tev3-content-wrapper">
                {children}
              </div>
            </Suspense>
          </Content>

          <Footer className="tev3-footer" style={{
            textAlign: 'center',
            padding: '24px 50px',
            background: token.colorBgContainer,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
          }}>
            <Typography.Text type="secondary">
              TEV <FontAwesomeIcon icon="fa-regular fa-copyright" /> Apache 2.0 {new Date().getFullYear()}
            </Typography.Text>
          </Footer>
        </Layout>
      </UserContext.Provider>
    </ConfigProvider>
  );
};

export default TEV3Layout;
